"use client";
import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { swrJsonFetcher } from "../../../hooks/swr/fetcher";

type Group = {
	id: string;
	wallets: number;
	profitable: number;
	profitable_pct: number | null;
	volume_usd: number | null;
	pnl_usd: number | null;
	roi_pct: number | null;
	roi_incl_unrealized_pct?: number | null;
	positions: number;
	fills: number;
	median_pnl_usd: number | null;
	median_volume_usd: number | null;
	median_followers: number | null;
	x_linked_pct: number | null;
	unrealized_pnl_usd?: number | null;
	open_cost_usd?: number | null;
	open_value_usd?: number | null;
	open_positions?: number | null;
	wallets_with_open?: number | null;
	pnl_incl_unrealized_usd?: number | null;
	median_unrealized_usd?: number | null;
	volume_30d_usd?: number | null;
	median_maker_share_pct?: number | null;
	median_hold_h?: number | null;
	median_buy_hold_pct?: number | null;
	median_active_days?: number | null;
	wallets_with_pnl?: number | null;
	rewards_usd?: number | null;
	maker_rebate_usd?: number | null;
	rewarded_pct?: number | null;
	merging_pct?: number | null;
};

type SegmentDef = {
	label: string;
	dimension: string;
	groups: Group[];
};

type Trader = {
	address: string;
	name: string | null;
	x_username: string | null;
	followers: number | null;
	taker_tier: string | null;
	created_at: string | null;
	age_days: number | null;
	fills_30d: number | null;
	volume_30d: number | null;
	total_positions: number | null;
	unique_markets: number | null;
	winning_positions: number | null;
	losing_positions: number | null;
	win_rate: number | null;
	volume_lifetime: number | null;
	pnl_lifetime: number | null;
	unrealized_pnl?: number | null;
	open_cost?: number | null;
	open_positions?: number | null;
	roi: number | null;
	roi_incl_unrealized?: number | null;
	total_fills: number | null;
	last_trade_time: string | null;
	persona?: string | null;
	maker_share?: number | null;
	top_category?: string | null;
	top_category_share?: number | null;
	hold_median_h?: number | null;
	bh_share?: number | null;
	active_days?: number | null;
	llm_persona?: string | null;
	llm_confidence?: number | null;
	llm_evidence?: string | null;
	has_pnl?: boolean | null;
	rewards_usd?: number | null;
	maker_rebate_usd?: number | null;
	merges?: number | null;
	merge_usd?: number | null;
	splits?: number | null;
	conversions?: number | null;
};

type SegmentsDoc = {
	generated_at: string;
	window: { days: number; ending: string };
	source: Record<string, unknown>;
	totals: Record<string, number>;
	segments: Record<string, SegmentDef>;
	top_traders: Trader[];
};

function money(v: number | null | undefined, digits = 1): string {
	if (v === null || v === undefined || Number.isNaN(v)) return "—";
	const a = Math.abs(v);
	const sign = v < 0 ? "-" : "";
	if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(digits)}B`;
	if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(digits)}M`;
	if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(digits)}K`;
	return `${sign}$${a.toFixed(0)}`;
}

function num(v: number | null | undefined): string {
	if (v === null || v === undefined || Number.isNaN(v)) return "—";
	return v.toLocaleString();
}

function pct(v: number | null | undefined, digits = 1): string {
	if (v === null || v === undefined || Number.isNaN(v)) return "—";
	return `${v.toFixed(digits)}%`;
}

function hours(v: number | null | undefined): string {
	if (v === null || v === undefined || Number.isNaN(v)) return "—";
	if (v < 1) return `${Math.round(v * 60)}m`;
	if (v < 48) return `${v.toFixed(1)}h`;
	return `${(v / 24).toFixed(1)}d`;
}

function share(v: number | null | undefined): string {
	return v === null || v === undefined ? "—" : pct(v * 100, 0);
}

function SegmentTable({ def }: { def: SegmentDef }) {
	// Behaviour columns exist only on segments built from the 30d fills pass.
	const hasBehavior = def.groups.some((g) => g.median_maker_share_pct != null);
	return (
		<div className="overflow-x-auto">
			<table className="table table-xs font-mono">
				<thead>
					<tr className="text-[10px] uppercase tracking-wider text-base-content/50">
						<th>{def.dimension}</th>
						<th className="text-right">wallets</th>
						<th className="text-right">profitable</th>
						<th className="text-right">volume</th>
						<th className="text-right">pnl</th>
						<th className="text-right">unrealized</th>
						<th className="text-right" title="lifetime pnl / lifetime money in">roi</th>
						<th className="text-right" title="(lifetime pnl + open positions marked to market) / money in">roi + unr.</th>
						<th className="text-right">positions</th>
						<th className="text-right">fills</th>
						<th className="text-right">median pnl</th>
						{hasBehavior && (
							<>
								<th className="text-right">vol 30d</th>
								<th className="text-right">maker</th>
								<th className="text-right">hold</th>
								<th className="text-right">b&amp;h</th>
								<th className="text-right">days</th>
								<th className="text-right" title="liquidity rewards, 30d">rewards</th>
								<th className="text-right" title="maker rebates, 30d">rebates</th>
								<th className="text-right" title="share of wallets earning liquidity rewards">rwd %</th>
								<th className="text-right" title="share of wallets merging complete sets">merge %</th>
							</>
						)}
					</tr>
				</thead>
				<tbody>
					{def.groups.map((g) => (
						<tr key={g.id} className="hover:bg-base-content/5">
							<td className="text-primary">{g.id}</td>
							<td className="text-right">{num(g.wallets)}</td>
							<td className="text-right">
								{pct(g.profitable_pct)}
								<span className="text-base-content/40">
									{" "}
									({num(g.profitable)}
									{g.wallets_with_pnl != null && g.wallets_with_pnl < g.wallets
										? ` of ${num(g.wallets_with_pnl)} with pnl`
										: ""}
									)
								</span>
							</td>
							<td className="text-right">{money(g.volume_usd)}</td>
							<td
								className={`text-right ${(g.pnl_usd ?? 0) >= 0 ? "text-success" : "text-error"}`}
								title="lifetime realised + marks of unredeemed bags on resolved markets"
							>
								{money(g.pnl_usd)}
							</td>
							<td
								className={`text-right ${(g.unrealized_pnl_usd ?? 0) >= 0 ? "text-success" : "text-error"}`}
								title={`open positions marked to live> bbo> catalogue; ${money(g.open_cost_usd)} cost basis`}
							>
								{money(g.unrealized_pnl_usd)}
							</td>
							<td
								className={`text-right ${(g.roi_pct ?? 0) >= 0 ? "text-success" : "text-error"}`}
							>
								{pct(g.roi_pct, 2)}
							</td>
							<td
								className={`text-right ${(g.roi_incl_unrealized_pct ?? 0) >= 0 ? "text-success" : "text-error"}`}
							>
								{pct(g.roi_incl_unrealized_pct, 2)}
							</td>
							<td className="text-right">{num(g.positions)}</td>
							<td className="text-right">{num(g.fills)}</td>
							<td className="text-right">{money(g.median_pnl_usd, 0)}</td>
							{hasBehavior && (
								<>
									<td className="text-right">{money(g.volume_30d_usd)}</td>
									<td className="text-right">{pct(g.median_maker_share_pct, 0)}</td>
									<td className="text-right">{hours(g.median_hold_h)}</td>
									<td className="text-right">{pct(g.median_buy_hold_pct, 0)}</td>
									<td className="text-right">{num(g.median_active_days)}</td>
									<td className="text-right">{money(g.rewards_usd)}</td>
									<td className="text-right">{money(g.maker_rebate_usd)}</td>
									<td className="text-right">{pct(g.rewarded_pct, 0)}</td>
									<td className="text-right">{pct(g.merging_pct, 0)}</td>
								</>
							)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function StatCard({
	label,
	value,
	sub,
	tone = "",
}: {
	label: string;
	value: string;
	sub?: string;
	tone?: string;
}) {
	return (
		<div className="card bg-base-200 border border-base-content/10">
			<div className="card-body p-3">
				<div className="text-[10px] font-mono uppercase tracking-wider text-base-content/50">
					{label}
				</div>
				<div className={`text-xl font-mono ${tone}`}>{value}</div>
				{sub && (
					<div className="text-[10px] font-mono text-base-content/40">{sub}</div>
				)}
			</div>
		</div>
	);
}

const CHARTS: {
	file: string;
	title: string;
	why: string;
	tall?: boolean;
}[] = [
	{
		file: "01_volume_pnl_scatter.png",
		title: "Segment map — volume × pnl",
		why: "The workhorse. Log volume on x, PnL on y, break-even line at 0: scale is horizontal position, profitability is vertical side, ROI is vertical distance. The amber line is the median PnL of each volume tier — that line crossing zero IS the segmentation finding.",
	},
	{
		file: "03_segmentation_matrix.svg",
		title: "Segmentation matrix — tier × tier",
		why: "For pure segmentation nothing beats a count matrix: every cell is a cohort you can name, with its size and its share of the population in the same cell.",
	},
	{
		file: "02_roi_by_tier_bubble.svg",
		title: "ROI by tier (bubble = cohort size)",
		why: "Collapses each segment to one number (ROI) so the sign flip across tiers is unmissable, while bubble area keeps the cohort size honest.",
	},
	{
		file: "04_profitability_by_tier.svg",
		title: "Winners vs losers by size",
		why: "Stacked counts + a % profitable line: answers 'how many' and 'what share' in one frame, which a scatter cannot.",
	},
	{
		file: "05_reach_vs_skill.png",
		title: "Reach vs skill — followers don't predict PnL",
		why: "The counter-intuitive cut: X audience against volume and PnL, split by profitable/unprofitable. Correlation ≈ 0 — a segment dimension that looks plausible and isn't.",
	},
	{
		file: "07_realized_vs_unrealized.png",
		title: "Realized vs unrealized — does the open book rescue the record?",
		why: "Left: every wallet with an open position, realized on x against unrealized on y, quadrants named. Right: the same split aggregated by volume tier. Unrealized = open (unresolved) positions marked to live > bbo > catalogue price; it never overlaps the realized side, which only marks unredeemed bags on resolved markets.",
	},
	{
		file: "08_open_exposure_by_tier.svg",
		title: "Where the open exposure sits",
		why: "Open cost basis per tier (bars) against unrealized as a share of that cost (line). 'priced %' is the share of the cohort's open positions that had any price rung — the honesty stamp on the mark.",
	},
];

function Charts() {
	return (
		<section className="mb-10">
			<div className="text-xs font-mono uppercase tracking-wider text-base-content/50 mb-3">
				charts — what actually separates the segments
			</div>
			<div className="grid grid-cols-1 gap-4">
				{CHARTS.map((c) => (
					<figure
						key={c.file}
						className="card bg-base-200 border border-base-content/10"
					>
						<div className="card-body p-4">
							<figcaption className="mb-2">
								<div className="text-sm font-mono text-primary">{c.title}</div>
								<div className="text-[11px] font-mono text-base-content/50 mt-1 max-w-4xl leading-relaxed">
									{c.why}
								</div>
							</figcaption>
							<a
								href={`data/charts/${c.file}`}
								target="_blank"
								rel="noopener noreferrer"
								className="block"
							>
								<img
									src={`data/charts/${c.file}`}
									alt={c.title}
									loading="lazy"
									className="w-full rounded border border-base-content/10 hover:border-primary/40 transition-colors"
								/>
							</a>
						</div>
					</figure>
				))}
				<div className="card bg-base-200 border border-base-content/10">
					<div className="card-body p-4">
						<div className="text-sm font-mono text-primary">
							3D explorer — volume × pnl × reach
						</div>
						<div className="text-[11px] font-mono text-base-content/50 mt-1 max-w-4xl leading-relaxed">
							Interactive and rotatable, but 3D scatter is an <em>exploration</em>{" "}
							device, not a reading one — occluded points and perspective make it
							easy to see structure that isn't there. Use it to hunt for clusters,
							then confirm them in the 2D views above.
						</div>
						<a
							href="data/charts/06_segment_explorer_3d.html"
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-sm btn-outline font-mono text-xs mt-3 self-start"
						>
							Open 3D explorer ↗
						</a>
					</div>
				</div>
			</div>
		</section>
	);
}

type CopyCandidate = {
	address: string;
	name: string | null;
	x_username: string | null;
	main_category: string | null;
	main_category_share: number | null;
	markets: number;
	copied_usd: number;
	their_roi: number | null;
	copy_1m: number | null;
	copy_5m: number | null;
	copy_30m: number | null;
	copy_5m_ci_lo: number | null;
	copy_5m_ci_hi: number | null;
	pre_roi: number | null;
	roi: number | null;
	avg_bet: number | null;
	win_rate: number | null;
	biggest_market: string | null;
	is_reference: boolean;
};

type CopyDoc = {
	generated_at: string;
	copy_window: { from: string; to: string };
	candidates: CopyCandidate[];
};

function roiCell(v: number | null | undefined, digits = 1) {
	return (
		<td className={`text-right ${(v ?? 0) >= 0 ? "text-success" : "text-error"}`}>
			{pct(v, digits)}
		</td>
	);
}

// Wallets whose buys stayed profitable when copied a few minutes later
// (marketing/copy_candidates.py): backtest on resolved markets, per-market ROI.
function CopyCandidates() {
	const { data } = useSWR<CopyDoc>("data/copy_candidates.json", swrJsonFetcher, {
		revalidateOnFocus: false,
	});
	if (!data) return null;
	const rows = data.candidates;
	const significant = rows.filter((r) => (r.copy_5m_ci_lo ?? 0) > 0).length;
	return (
		<section className="card bg-base-200 border border-base-content/10 mb-10">
			<div className="card-body p-4">
				<div className="text-xs font-mono uppercase tracking-wider text-base-content/50">
					copy candidates — would buying after them have paid?
				</div>
				<p className="text-[11px] font-mono text-base-content/60 max-w-4xl leading-relaxed">
					Every buy these wallets made on markets that resolved between{" "}
					{data.copy_window.from} and {data.copy_window.to}, re-priced at the next
					trade 1, 5 and 30 minutes later. ROI is per market (fills of one bet are
					not independent); the range is a bootstrap 90% interval for the 5-minute
					copy. {significant} of {rows.length} wallets stay above zero at 90%. "pre"
					is ROI on markets that ended before the window — what you could have
					known when picking them. Not included: your own price impact, taker fees.
				</p>
				<div className="overflow-x-auto">
					<table className="table table-xs font-mono">
						<thead>
							<tr className="text-[10px] uppercase tracking-wider text-base-content/50">
								<th>wallet</th>
								<th>x</th>
								<th>category</th>
								<th className="text-right">markets</th>
								<th className="text-right">theirs</th>
								<th className="text-right">copy 1m</th>
								<th className="text-right">copy 5m</th>
								<th className="text-right">5m 90% range</th>
								<th className="text-right">copy 30m</th>
								<th className="text-right" title="ROI on markets that ended before the window">pre</th>
								<th className="text-right">avg bet</th>
								<th>biggest market</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => (
								<tr
									key={r.address}
									className={`hover:bg-base-content/5 ${r.is_reference ? "bg-primary/10" : ""}`}
								>
									<td>
										<a
											href={`https://polymarket.com/profile/${r.address}`}
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary hover:underline"
										>
											{r.name ?? `${r.address.slice(0, 10)}…`}
										</a>
										{r.is_reference && (
											<span className="text-base-content/40"> (example)</span>
										)}
									</td>
									<td>
										{r.x_username ? (
											<a
												href={`https://x.com/${r.x_username}`}
												target="_blank"
												rel="noopener noreferrer"
												className="hover:underline"
											>
												@{r.x_username}
											</a>
										) : (
											"—"
										)}
									</td>
									<td>
										{r.main_category ?? "—"}
										{r.main_category_share != null && (
											<span className="text-base-content/40">
												{" "}
												{pct(r.main_category_share, 0)}
											</span>
										)}
									</td>
									<td className="text-right">{num(r.markets)}</td>
									{roiCell(r.their_roi)}
									{roiCell(r.copy_1m)}
									{roiCell(r.copy_5m)}
									<td className="text-right text-base-content/60">
										{pct(r.copy_5m_ci_lo, 0)} … {pct(r.copy_5m_ci_hi, 0)}
									</td>
									{roiCell(r.copy_30m)}
									{roiCell(r.pre_roi)}
									<td className="text-right">{money(r.avg_bet, 1)}</td>
									<td className="text-base-content/60 max-w-[260px] truncate" title={r.biggest_market ?? ""}>
										{r.biggest_market ?? "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}

const SORTERS: Record<string, (a: Trader, b: Trader) => number> = {
	pnl: (a, b) => (b.pnl_lifetime ?? 0) - (a.pnl_lifetime ?? 0),
	volume: (a, b) => (b.volume_lifetime ?? 0) - (a.volume_lifetime ?? 0),
	followers: (a, b) => (b.followers ?? 0) - (a.followers ?? 0),
	trades: (a, b) => (b.total_fills ?? 0) - (a.total_fills ?? 0),
	roi: (a, b) => (b.roi ?? 0) - (a.roi ?? 0),
	maker: (a, b) => (b.maker_share ?? -1) - (a.maker_share ?? -1),
	llm: (a, b) => (b.llm_confidence ?? -1) - (a.llm_confidence ?? -1),
	rewards: (a, b) =>
		(b.rewards_usd ?? 0) + (b.maker_rebate_usd ?? 0) - ((a.rewards_usd ?? 0) + (a.maker_rebate_usd ?? 0)),
};

const TOP_TRADERS_ANCHOR = "top-traders";

export function SegmentsPage() {
	const { data, error } = useSWR<SegmentsDoc>(
		"data/segments.json",
		swrJsonFetcher,
		{ revalidateOnFocus: false },
	);
	const [sort, setSort] = useState<keyof typeof SORTERS>("pnl");

	// The section renders only after segments.json loads, so the browser's own
	// jump to /segments#top-traders finds nothing; scroll once it exists.
	useEffect(() => {
		if (data && window.location.hash === `#${TOP_TRADERS_ANCHOR}`) {
			document.getElementById(TOP_TRADERS_ANCHOR)?.scrollIntoView();
		}
	}, [data]);

	const totals = data?.totals;
	const traders = useMemo(() => {
		const rows = [...(data?.top_traders ?? [])];
		rows.sort(SORTERS[sort] ?? SORTERS.pnl);
		return rows.slice(0, 200);
	}, [data, sort]);

	return (
		<div className="min-h-screen bg-base-100">
			<main className="container mx-auto max-w-7xl px-4 py-8">
				<header className="mb-6">
					<h1 className="text-2xl font-mono text-primary">
						◆ PolyGains Segments
					</h1>
					<p className="text-xs font-mono text-base-content/60 mt-1 max-w-3xl">
						Trader segments on Polymarket, cut from the last{" "}
						{data?.window?.days ?? 30} days of fills (window ends{" "}
						{data?.window?.ending?.slice(0, 10) ?? "…"}). Segments are keyed by
						the wallets' public x.com handle where the trader published one —
						the handle is resolved from the same profile payload
						polymarket.com/profile/0x… renders.
					</p>
					<p className="text-xs font-mono text-base-content/60 mt-1 max-w-3xl">
						Behaviour segments come from each wallet's own 30d fills: maker = share
						of fills as the resting order, hold = median time from first buy to exit
						(or market end), b&amp;h = share of tokens bought and never sold. X
						follower counts cover the profiles scraped so far.
					</p>
					<p className="text-xs font-mono text-base-content/60 mt-1 max-w-3xl">
						Personas (first match wins): bot = active in 20+ of 24 UTC hours with
						1,000+ fills · market maker = 60%+ maker fills, 100+ fills, sells 25%+
						of what it buys · casual retail = under 5 fills, or under 20 small
						taker fills · longshot gambler = 40%+ of buy volume at 15¢ or less ·
						then buy &amp; hold, scalper, swing. The llm column is an independent
						DeepSeek label on a 1k stratified sample; hover for its evidence.
					</p>
					{error && (
						<div className="text-xs font-mono text-error mt-2">
							Couldn't load segments.json ({String(error.message ?? error)}).
						</div>
					)}
					{!data && !error && (
						<div className="flex items-center gap-2 text-xs font-mono text-base-content/50 mt-3">
							<span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
							<span>Loading segments…</span>
						</div>
					)}
				</header>

				{totals && (
					<>
						<Charts />

						<section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
							<StatCard
								label="x-linked wallets"
								value={num(totals.x_linked_wallets)}
								sub={`of ${num(totals.active_wallets_30d)} active in 30d`}
							/>
							<StatCard
								label="profitable"
								value={pct(totals.x_linked_profitable_pct)}
								sub={`${num(totals.x_linked_profitable)} wallets lifetime PnL > 0`}
							/>
							<StatCard
								label="lifetime volume (money in)"
								value={money(totals.x_linked_volume_usd)}
								sub={`${money(totals.x_linked_volume_30d_usd)} in the last 30d`}
							/>
							<StatCard
								label="lifetime pnl"
								value={money(totals.x_linked_pnl_usd)}
								tone={(totals.x_linked_pnl_usd ?? 0) >= 0 ? "text-success" : "text-error"}
								sub={`roi ${pct(
									((totals.x_linked_pnl_usd ?? 0) /
										(totals.x_linked_volume_usd || 1)) *
										100,
									2,
								)}`}
							/>
							<StatCard
								label="positions"
								value={num(totals.x_linked_positions)}
								sub="lifetime, settled + open"
							/>
							<StatCard
								label="trades (fills)"
								value={num(totals.x_linked_fills)}
								sub={`${num(totals.x_linked_fills_30d)} in the last 30d`}
							/>
							<StatCard
								label="x reach scraped"
								value={num(totals.x_reach_scraped)}
								sub={`${num(totals.x_followers_total)} followers combined`}
							/>
							<StatCard
								label="all active pnl"
								value={money(totals.all_active_pnl_usd)}
								sub={`on ${money(totals.all_active_volume_usd)} volume`}
							/>
							<StatCard
								label="unrealized (open book)"
								value={money(totals.x_linked_unrealized_usd)}
								tone={
									(totals.x_linked_unrealized_usd ?? 0) >= 0
										? "text-success"
										: "text-error"
								}
								sub={`x-linked · ${money(totals.x_linked_open_cost_usd)} cost · ${num(totals.x_linked_open_positions)} open positions`}
							/>
							<StatCard
								label="pnl incl. unrealized"
								value={money(totals.x_linked_pnl_incl_unrealized_usd)}
								tone={
									(totals.x_linked_pnl_incl_unrealized_usd ?? 0) >= 0
										? "text-success"
										: "text-error"
								}
								sub={`realised ${money(totals.x_linked_pnl_usd)} + open mark ${money(totals.x_linked_unrealized_usd)} · roi ${pct(totals.x_linked_roi_incl_unrealized_pct, 2)}`}
							/>
							<StatCard
								label="price coverage"
								value={pct(totals.unrealized_price_coverage_pct)}
								sub={`of open positions carry a live/bbo/catalogue mark · ${pct(totals.unrealized_price_coverage_by_cost_pct)} of cost basis`}
							/>
							<StatCard
								label="liquidity rewards 30d"
								value={money(totals.x_linked_rewards_usd)}
								tone="text-success"
								sub={`${num(totals.x_linked_rewarded_wallets)} x-linked wallets paid`}
							/>
							<StatCard
								label="maker rebates 30d"
								value={money(totals.x_linked_rebates_usd)}
								tone="text-success"
								sub={`${num(totals.x_linked_rebated_wallets)} x-linked wallets paid`}
							/>
							<StatCard
								label="merged 30d"
								value={money(totals.x_linked_merge_usd)}
								sub={`${num(totals.x_linked_merging_wallets)} x-linked wallets merged complete sets`}
							/>
							<StatCard
								label="all active rewards 30d"
								value={money(totals.all_active_rewards_usd)}
								tone="text-success"
								sub={`${num(totals.all_active_rewarded_wallets)} wallets · ${
									(totals.incentives_wallets ?? 0) < (totals.active_wallets_30d ?? 0)
										? `${num(totals.incentives_wallets)} of ${num(totals.active_wallets_30d)} fetched`
										: "all active wallets"
								}`}
							/>
							<StatCard
								label="all active rebates 30d"
								value={money(totals.all_active_rebates_usd)}
								tone="text-success"
								sub={`${num(totals.all_active_rebated_wallets)} wallets · merged ${money(totals.all_active_merge_usd)}`}
							/>
						</section>

						<section className="space-y-6 mb-10">
							{Object.entries(data.segments).map(([key, def]) => (
								<div
									key={key}
									className="card bg-base-200 border border-base-content/10"
								>
									<div className="card-body p-4">
										<div className="text-xs font-mono uppercase tracking-wider text-base-content/50 mb-2">
											{def.label}
										</div>
										<SegmentTable def={def} />
									</div>
								</div>
							))}
						</section>
					</>
				)}

				<CopyCandidates />
				{data && (
					<section
						id={TOP_TRADERS_ANCHOR}
						className="card bg-base-200 border border-base-content/10 scroll-mt-20"
					>
						<div className="card-body p-4">
							<div className="flex items-center justify-between flex-wrap gap-2 mb-2">
								<a
									href={`#${TOP_TRADERS_ANCHOR}`}
									className="group text-xs font-mono uppercase tracking-wider text-base-content/50 hover:text-primary"
								>
									top x-linked traders by {sort}
									<span className="ml-1 opacity-0 group-hover:opacity-100">#</span>
								</a>
								<div className="flex gap-2 flex-wrap">
									{(Object.keys(SORTERS) as (keyof typeof SORTERS)[]).map((k) => (
										<button
											key={k}
											type="button"
											onClick={() => setSort(k)}
											className={`btn btn-xs font-mono ${sort === k ? "btn-primary" : "btn-ghost"}`}
										>
											{k}
										</button>
									))}
								</div>
							</div>
							<div className="overflow-x-auto">
								<table className="table table-xs font-mono">
									<thead>
										<tr className="text-[10px] uppercase tracking-wider text-base-content/50">
											<th>#</th>
											<th>trader</th>
											<th>x</th>
											<th className="text-right">followers</th>
											<th className="text-right">pnl</th>
											<th className="text-right">unreal.</th>
											<th className="text-right">roi</th>
											<th className="text-right" title="incl. open positions marked to market">roi + unr.</th>
											<th className="text-right">volume</th>
											<th className="text-right">positions</th>
											<th className="text-right">fills</th>
											<th className="text-right">fills 30d</th>
											<th>persona</th>
											<th title="DeepSeek label, 1k stratified sample">llm</th>
											<th>category</th>
											<th className="text-right">maker</th>
											<th className="text-right">hold</th>
											<th className="text-right">b&amp;h</th>
											<th className="text-right" title="liquidity rewards + maker rebates, 30d">rwd+reb</th>
											<th className="text-right" title="merges / splits / neg-risk conversions, 30d">m/s/c</th>
											<th>tier</th>
										</tr>
									</thead>
									<tbody>
										{traders.map((t, i) => (
											<tr key={t.address} className="hover:bg-base-content/5">
												<td className="text-base-content/40">{i + 1}</td>
												<td>
													<a
														href={`https://polymarket.com/profile/${t.address}`}
														target="_blank"
														rel="noopener noreferrer"
														className="text-primary hover:underline"
													>
														{t.name ?? t.address.slice(0, 10)}
													</a>
												</td>
												<td>
													{t.x_username ? (
														<a
															href={`https://x.com/${t.x_username}`}
															target="_blank"
															rel="noopener noreferrer"
															className="hover:underline"
														>
															@{t.x_username}
														</a>
													) : (
														"—"
													)}
												</td>
												<td className="text-right">{num(t.followers)}</td>
												<td
													className={`text-right ${(t.pnl_lifetime ?? 0) >= 0 ? "text-success" : "text-error"}`}
												>
													{money(t.pnl_lifetime, 0)}
												</td>
												<td className="text-right">
													{money(t.unrealized_pnl, 0)}
												</td>
												<td className="text-right">{pct(t.roi, 1)}</td>
												<td className="text-right">{pct(t.roi_incl_unrealized, 1)}</td>
												<td className="text-right">{money(t.volume_lifetime)}</td>
												<td className="text-right">{num(t.total_positions)}</td>
												<td className="text-right">{num(t.total_fills)}</td>
												<td className="text-right">{num(t.fills_30d)}</td>
												<td>{t.persona?.replaceAll("_", " ") ?? "—"}</td>
												<td title={t.llm_evidence ?? undefined}>
													{t.llm_persona ? (
														<>
															{t.llm_persona.replaceAll("_", " ")}
															<span className="text-base-content/40">
																{" "}
																{share(t.llm_confidence)}
															</span>
														</>
													) : (
														"—"
													)}
												</td>
												<td>
													{t.top_category ?? "—"}
													{t.top_category_share != null && (
														<span className="text-base-content/40">
															{" "}
															{share(t.top_category_share)}
														</span>
													)}
												</td>
												<td className="text-right">{share(t.maker_share)}</td>
												<td className="text-right">{hours(t.hold_median_h)}</td>
												<td className="text-right">{share(t.bh_share)}</td>
												<td
													className="text-right"
													title={`rewards ${money(t.rewards_usd)} · rebates ${money(t.maker_rebate_usd)}`}
												>
													{money((t.rewards_usd ?? 0) + (t.maker_rebate_usd ?? 0))}
												</td>
												<td className="text-right" title={`merged ${money(t.merge_usd)}`}>
													{num(t.merges)}/{num(t.splits)}/{num(t.conversions)}
												</td>
												<td className="text-base-content/50">
													{t.taker_tier ?? "—"}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<div className="text-[10px] font-mono text-base-content/40 mt-2">
								showing top {traders.length} of {num(data.top_traders.length)}{" "}
								rows embedded · full {data.totals.x_linked_wallets?.toLocaleString()}
								-wallet table:{" "}
								<a href="data/segments-traders.json.zst" className="hover:underline">
									segments-traders.json.zst
								</a>{" "}
								(unpack with <code>zstd -d</code>)
							</div>
						</div>
					</section>
				)}

				<footer className="flex items-center justify-between gap-4 flex-wrap pt-6 pb-8 mt-8 border-t border-base-content/10 text-xs font-mono text-base-content/60">
					<div className="flex items-center gap-3 flex-wrap">
						<a href="/" className="hover:text-primary transition-colors">
							◆ Terminal
						</a>
						<span>•</span>
						<a href="/dashboard" className="hover:text-primary transition-colors">
							📈 Market Dashboard
						</a>
						<span>•</span>
						<a
							href="https://dash.polygains.com"
							target="_blank"
							rel="noopener noreferrer"
							className="hover:text-primary transition-colors"
						>
							Metabase ↗
						</a>
					</div>
					{data && (
						<div className="text-base-content/40">
							generated {data.generated_at.slice(0, 16).replace("T", " ")} UTC
						</div>
					)}
				</footer>
			</main>
		</div>
	);
}

export default SegmentsPage;
